import { useCallback, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import { listRequest, reportRequest } from 'core/services/admin';
import { searchRequest } from 'core/services/product';
import { EntityTypes } from 'constants/entities';
import { IReportFilter, ReportType } from 'core/slices/report';
import { SearchType } from 'types/domain/search';

interface UsePaginationOptions {
  entityType: EntityTypes;
  componentId: string;
  reportType?: ReportType;
  parentEntityId?: number;
  watchFilters?: boolean; // Option to watch for search/filter changes
  useSearch?: boolean; // Use searchRequest instead of listRequest
  searchType?: SearchType; // SearchType for searchRequest
  reportFilter?: IReportFilter;
}

export const usePagination = ({
  entityType,
  componentId,
  reportType,
  parentEntityId,
  watchFilters = true, // Default to true
  useSearch = false, // Default to false
  searchType,
  reportFilter,
}: UsePaginationOptions) => {
  const dispatch = useAppDispatch();

  // Watch for search term and report filter changes if enabled
  // Normalize to '' so the undefined→'' transition (caused by listRequest.pending initializing
  // the component entry in the search slice) doesn't trigger a spurious second fetch.
  const searchTerm = useAppSelector(state => state.core.search.components[componentId]?.searchTerm ?? '');
  const filter = useAppSelector(state => state.core.report.custom.filter);

  // Read current pageSize from Redux state to avoid stale closures
  const currentPageSize = useAppSelector(state => state.core.search.components[componentId]?.pageSize || 10);

  // Helper function to dispatch list, report, or search request
  const fetchData = useCallback(
    (page: number, size: number) => {
      // Use searchRequest if useSearch is enabled
      if (useSearch) {
        return dispatch(
          searchRequest({
            term: searchTerm || '',
            searchType: searchType || SearchType.ORG_USER,
            pagerInfo: {
              page,
              pageCount: size,
            },
            componentId,
          }),
        );
      }

      // Use reportRequest if reportType is provided
      if (reportType !== undefined) {
        return dispatch(
          reportRequest({
            type: entityType,
            reportType,
            parentEntityId,
            search: searchTerm || undefined,
            filter: reportFilter || filter,
            pagerInfo: {
              page,
              pageCount: size,
            },
            componentId,
          }),
        );
      }

      // Otherwise use listRequest
      return dispatch(
        listRequest({
          type: entityType,
          filter: {
            parentEntityId,
            ...(searchTerm && { search: searchTerm }),
            ...reportFilter,
          },
          pagerInfo: {
            page,
            pageCount: size,
          },
          componentId,
        }),
      );
    },
    [
      useSearch,
      reportType,
      dispatch,
      entityType,
      parentEntityId,
      searchTerm,
      reportFilter,
      componentId,
      searchType,
      filter,
    ],
  );

  // Initial load on mount
  useEffect(() => {
    fetchData(0, currentPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track whether this is the first run of the filter-watching effect so we
  // can skip it on mount (the initial load above already covers that case).
  const isInitialFilterRun = useRef(true);

  // Re-fetch when search term or filters change (if watchFilters is enabled)
  useEffect(() => {
    if (isInitialFilterRun.current) {
      isInitialFilterRun.current = false;
      return;
    }
    if (watchFilters) {
      fetchData(0, currentPageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, reportFilter, watchFilters]);

  // Pagination handlers
  const handlePageChange = useCallback(
    (page: number) => {
      console.log(page);
      fetchData(page, currentPageSize);
    },
    [fetchData, currentPageSize],
  );

  const handlePageSizeChange = useCallback(
    (size: number) => {
      console.log(size);
      fetchData(0, size);
    },
    [fetchData],
  );

  // Helper to reset to first page
  const resetToFirstPage = useCallback(() => {
    fetchData(0, currentPageSize);
  }, [fetchData, currentPageSize]);

  return {
    fetchData,
    handlePageChange,
    handlePageSizeChange,
    resetToFirstPage,
  };
};
